/**
 * AppRoleBadge — Displays BeHeard app role profile badges
 *
 * Shows the resolved BeHeard role label(s) for a user.
 * This is DISPLAY ONLY — it does not control authorization.
 */

import { type AppRoleKey, APP_ROLE_PROFILES } from '../hooks/useAppRole';

const BADGE_COLORS: Record<string, string> = {
  purple: 'bg-purple-100 text-purple-800',
  indigo: 'bg-indigo-100 text-indigo-800',
  blue: 'bg-blue-100 text-blue-800',
  amber: 'bg-amber-100 text-amber-800',
  rose: 'bg-rose-100 text-rose-800',
  teal: 'bg-teal-100 text-teal-800',
  green: 'bg-green-100 text-green-800',
  slate: 'bg-slate-100 text-slate-700',
  gray: 'bg-gray-100 text-gray-600',
};

const profilesByKey = new Map(APP_ROLE_PROFILES.map((p) => [p.key, p]));

interface AppRoleBadgeProps {
  profileKey: AppRoleKey;
  size?: 'sm' | 'md';
}

export function AppRoleBadge({ profileKey, size = 'sm' }: AppRoleBadgeProps) {
  const profile = profilesByKey.get(profileKey);
  if (!profile) return null;

  const colorClass = BADGE_COLORS[profile.badgeColor] ?? BADGE_COLORS.gray;
  const sizeClass = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-sm px-2.5 py-1';

  return (
    <span className={`inline-flex items-center rounded-full font-medium ${colorClass} ${sizeClass}`}>
      {profile.displayName}
    </span>
  );
}

interface AppRoleBadgeListProps {
  profileKeys: AppRoleKey[];
  size?: 'sm' | 'md';
}

export function AppRoleBadgeList({ profileKeys, size = 'sm' }: AppRoleBadgeListProps) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {profileKeys.map((key) => (
        <AppRoleBadge key={key} profileKey={key} size={size} />
      ))}
    </div>
  );
}
